import { useEffect, useRef, useState } from "react";
import { useTimeout } from "usehooks-ts";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSToggle,
} from "@/mds";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import { CalendarConnectionsSection } from "@/components/calendar/calendar-connections-section";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
  SettingsIntegrationBenefitCard,
} from "@/pages/settings/components/settings-integration-layout";

const BENEFIT_POINTS = [
  "Connect any Google or Microsoft calendar",
  "See current and upcoming events in Mem, and get notified when meetings are about to start",
  "Have Mem record and take meeting notes for you in a single click",
  "Edit the notes Mem takes for you anytime",
];

type OptimisticEventVisibilityState = {
  value: boolean;
  requestId: number;
};

export const SettingsCalendarPage = observer(() => {
  const { store } = useAppStore();
  const isConnected = store.calendar.google.isConnected || store.calendar.outlook.isConnected;
  const isLoading = !store.calendar.google.isReady || !store.calendar.outlook.isReady;
  const syncedShowCalendarEventsWithNoAttendees =
    store.spaceAccountSettings.calendar.showCalendarEventsWithNoAttendees;

  const [isOAuthPending, setIsOAuthPending] = useState(false);
  const [optimisticEventVisibility, setOptimisticEventVisibility] =
    useState<OptimisticEventVisibilityState>();
  const eventVisibilityRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  const showCalendarEventsWithNoAttendees =
    optimisticEventVisibility?.value ?? syncedShowCalendarEventsWithNoAttendees;
  const isEventVisibilitySavePending = optimisticEventVisibility !== undefined;

  useTimeout(() => setIsOAuthPending(false), isOAuthPending ? 5000 : null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Apps and integrations"
        title="Calendar and meetings"
        isConnected={!isLoading ? isConnected : undefined}
      />

      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <SettingsIntegrationBenefitCard points={BENEFIT_POINTS} />
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <CalendarConnectionsSection
            onConnectClick={() => setIsOAuthPending(true)}
            isOAuthPending={isOAuthPending}
          />
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <SettingsSectionLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            EVENT VISIBILITY
          </SettingsSectionLabel>
          <SettingsToggleRow>
            <MDSText variant={MDSTextVariant.ParagraphS}>
              Show calendar events with no attendees
            </MDSText>
            <MDSToggle
              aria-label="Show calendar events with no attendees"
              checked={showCalendarEventsWithNoAttendees}
              disabled={isEventVisibilitySavePending}
              onChange={event => {
                const nextShowCalendarEventsWithNoAttendees = event.currentTarget.checked;
                const requestId = eventVisibilityRequestIdRef.current + 1;
                eventVisibilityRequestIdRef.current = requestId;
                setOptimisticEventVisibility({
                  value: nextShowCalendarEventsWithNoAttendees,
                  requestId,
                });

                void store.spaceAccountSettings
                  .updateSettingsUsingApi({
                    calendar: {
                      show_calendar_events_with_no_attendees: nextShowCalendarEventsWithNoAttendees,
                    },
                  })
                  .then(
                    () => {
                      if (!isMountedRef.current) return;

                      setOptimisticEventVisibility(currentOptimisticEventVisibility => {
                        if (currentOptimisticEventVisibility?.requestId !== requestId) {
                          return currentOptimisticEventVisibility;
                        }

                        return undefined;
                      });
                    },
                    () => {
                      if (eventVisibilityRequestIdRef.current !== requestId) return;

                      if (isMountedRef.current) {
                        setOptimisticEventVisibility(undefined);
                      }
                      toastModule.triggerToast({
                        content: "Couldn't save event visibility. Please try again.",
                      });
                    }
                  );
              }}
            />
          </SettingsToggleRow>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <SettingsSectionLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            RELATED SETTINGS
          </SettingsSectionLabel>
          <RelatedSettingsRow>
            <MDSButton
              variant={MDSButtonVariant.Default}
              onClick={() => store.settingsModal.navigateToTab(SettingsTabId.VoiceModePage)}
            >
              Voice mode settings
              <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.ArrowUpRight} />
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Default}
              onClick={() =>
                store.settingsModal.navigateToTab(SettingsTabId.NotificationPreferencesPage)
              }
            >
              Meeting notification settings
              <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.ArrowUpRight} />
            </MDSButton>
          </RelatedSettingsRow>
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const SettingsSectionLabel = styled(MDSText)`
  padding-top: ${({ theme }) => theme.spacing[4]};
  padding-bottom: 10px;
`;

const SettingsToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  max-width: 700px;
`;

const RelatedSettingsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  max-width: 700px;

  @container mds-panel (max-width: 750px) {
    flex-direction: column;

    > * {
      width: 100%;
    }
  }
`;
